import { ViewHook } from "phoenix_live_view";

import type { ComposeOption } from "echarts/core";
import type { PieSeriesOption } from "echarts/charts";
import type { TooltipComponentOption } from "echarts/components";

import type { ChartHandle } from "../charts/echarts";
import { FONT_FAMILY } from "../constants";
import {
  whenVisible,
  afterInitialResizeNotification,
  type VisibilityWait,
} from "../utils/charts";
import { pageLocale } from "../utils/formatters";

type ChartOption = ComposeOption<PieSeriesOption | TooltipComponentOption>;

const weightPercentFormatter = new Intl.NumberFormat(pageLocale(), {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatWeightPercent = (weight: number) => weightPercentFormatter.format(weight);

interface DonutDatum {
  locked?: boolean;
  label: string;
  value: number;
}

interface EChartsTooltipParam {
  dataIndex: number;
}

const defaultColors = [
  "#2563eb",
  "#e11d48",
  "#fbbf24",
  "#a3e635",
  "#059669",
  "#7c3aed",
  "#22d3ee",
  "#ec4899",
  "#facc15",
  "#2dd4bf",
  "#8b5cf6",
  "#155dfc",
  "#c800de",
  "#ec003f",
];

class DonutChart extends ViewHook {
  private chartHandle?: ChartHandle;
  private isDestroyed?: boolean;

  // Definite assignment: mounted() always runs first and sets these.
  private chartData!: DonutDatum[];
  private colors?: string[];
  private arcWidth!: number;
  private animateWhenVisible!: boolean;

  private visibilityWait?: VisibilityWait;

  // LiveView ignores mounted()'s return value, so the promise is safe to return.
  // oxlint-disable-next-line typescript/no-misused-promises
  async mounted() {
    this.chartData = this.parseChartData();
    this.colors = this.parseChartColors();
    this.arcWidth = Math.trunc(Number(this.el.dataset.arcWidth ?? "0"));
    this.animateWhenVisible = "animateWhenVisible" in this.el.dataset;

    if (this.animateWhenVisible) {
      this.visibilityWait = whenVisible(this.el, 1);
      await this.visibilityWait.visible;
      this.visibilityWait = undefined;
    }

    // Imported lazily so pages without charts never download echarts.
    const { createChart } = await import("../charts/echarts");

    if (this.isDestroyed) {
      return;
    }

    this.chartHandle = createChart(this.el, {
      onResize: (chart) => chart.setOption({ series: [{ radius: this.radius() }] }),
    });

    if (this.animateWhenVisible) {
      await afterInitialResizeNotification();
    }

    this.chartHandle.chart.setOption(this.buildOption());
  }

  updated() {
    this.chartData = this.parseChartData();

    this.chartHandle?.chart.setOption({ series: [{ data: this.seriesData() }] });
  }

  destroyed() {
    this.isDestroyed = true;

    this.visibilityWait?.cancel();
    this.visibilityWait = undefined;

    this.chartHandle?.destroy();
    this.chartHandle = undefined;
  }

  private buildOption(): ChartOption {
    return {
      textStyle: {
        fontFamily: FONT_FAMILY,
      },
      tooltip: {
        trigger: "item",
        backgroundColor: "var(--chart-tooltip-background-color)",
        borderColor: "var(--chart-tooltip-border-color)",
        textStyle: {
          color: "var(--chart-tooltip-text-color)",
        },
        padding: [6, 12],
        formatter: ((params: EChartsTooltipParam) =>
          this.tooltipFormatter(params)) as unknown as TooltipComponentOption["formatter"],
        extraCssText: "z-index: 20",
      },
      series: [
        {
          type: "pie",
          radius: this.radius(),
          // Matches the previous unovis rendering: 0.03 rad pad angle, no hover
          // scaling, no per-segment labels.
          padAngle: 1.7,
          emphasis: { scale: false },
          label: { show: false },
          labelLine: { show: false },
          data: this.seriesData(),
        },
      ],
    };
  }

  private seriesData() {
    return this.chartData.map((datum, index) => ({
      value: datum.value,
      name: datum.label,
      itemStyle: { color: this.colorAt(index) },
    }));
  }

  private colorAt(index: number): string {
    return (this.colors ?? defaultColors)[index] ?? "#2563eb";
  }

  // The arc fills the container, with `arcWidth` px of ring thickness
  // (0 renders a full pie) — same geometry as the previous unovis donut.
  private radius(): [number, number] {
    const outerRadius = Math.min(this.el.clientWidth, this.el.clientHeight) / 2;
    const innerRadius = this.arcWidth === 0 ? 0 : Math.max(outerRadius - this.arcWidth, 0);

    return [innerRadius, outerRadius];
  }

  private tooltipFormatter(params: EChartsTooltipParam) {
    const datum = this.chartData[params.dataIndex];

    const locked = datum.locked ?? false;
    const label = locked ? (this.el.dataset.lockedLabel as string) : datum.label;

    let result = "";

    result += `<div class="flex items-center justify-center">`;
    result += `<div class="w-2 h-2 rounded-full" style="background-color: ${this.colorAt(params.dataIndex)};"></div>`;
    result += `<div class="ml-2 font-bold">${label}</div>`;

    if (!locked) {
      result += `<div class="ml-4 text-secondary">${formatWeightPercent(datum.value)}</div>`;
    }

    result += `</div>`;

    return result;
  }

  private parseChartData(): DonutDatum[] {
    const { chartData: chartDataStr } = this.el.dataset;

    if (!chartDataStr) {
      console.error("DonutChart: missing chartData");
      return [];
    }

    return JSON.parse(chartDataStr) as DonutDatum[];
  }

  private parseChartColors(): string[] | undefined {
    const { colors: colorsStr } = this.el.dataset;

    if (!colorsStr) {
      return undefined;
    }

    return JSON.parse(colorsStr) as string[];
  }
}

export default DonutChart;
